import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { Paper, Group, Stack, Text, Button } from '@mantine/core'
import { IconMap2, IconMessage } from '@tabler/icons-react'
import { isAuthenticatedAtom, isProvisionalAtom } from '../../atoms/authAtoms'
import { playerSessionService } from '../../services/playerSessionService'

/**
 * Surfaces a returning student's most recently completed story so they can jump
 * straight back in to revisit it and keep chatting with the characters. Once a
 * story is completed the catalog offers no obvious "what now" path; this is that
 * path. Links to the same read-only review view as the Profile Review button
 * (/review/:sessionId) — the story stays completed; character chat is live
 * inside it.
 *
 * Mirrors TeamPlayBanner: returns null when there's nothing to show.
 */
export function RevisitBanner() {
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isProvisional = useAtomValue(isProvisionalAtom)

  // Shares the ['completedSessions'] queryKey with Profile so React Query
  // dedupes/caches the fetch across both surfaces.
  const { data: completedSessions = [] } = useQuery({
    queryKey: ['completedSessions'],
    queryFn: () => playerSessionService.getCompletedSessions(),
    enabled: isAuthenticated && !isProvisional,
    staleTime: 60 * 1000,
  })

  if (!completedSessions.length) return null

  // Most recently completed session (don't assume backend ordering).
  const latest = [...completedSessions].sort(
    (a, b) => new Date(b.completed_at) - new Date(a.completed_at)
  )[0]

  if (!latest) return null

  return (
    <Paper
      p="md"
      radius="md"
      style={{
        background: 'rgba(34, 139, 230, 0.10)',
        border: '1px solid var(--mantine-color-cyan-7)',
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconMap2 size={22} color="var(--mantine-color-cyan-4)" style={{ flexShrink: 0 }} />
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="cyan.2">
              {latest.story_name
                ? `You finished "${latest.story_name}"`
                : 'You finished your story'}
            </Text>
            <Text size="xs" c="dimmed">
              Revisit your journey and keep talking to the characters.
            </Text>
          </Stack>
        </Group>
        <Button
          color="cyan"
          leftSection={<IconMessage size={16} />}
          onClick={() => navigate(`/review/${latest.id}`)}
          style={{ flexShrink: 0 }}
        >
          Revisit & keep chatting
        </Button>
      </Group>
    </Paper>
  )
}
